import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(theme => ({
  gradientBar: {
    width: (props) => props.maxScale === 10 ? '500px' : '380px',
    height: '24px',
    background: (props) => props.maxScale === 10 
      ? `linear-gradient(90deg, #C51407 0%, #E2483C 11.11%, #F5B847 22.22%, #F5B847 33.33%, #F5B847 44.44%, #F5B847 55.56%, #F5B847 66.67%, #F5B847 77.78%, #009970 88.89%, #00664A 100%)`
      : `linear-gradient(90deg, #C51407 0%, #E2483C 25.36%, #F5B847 50.33%, #009970 75.12%, #00664A 100%)`,
    border: `1px solid ${theme.palette.neutral['clr-400']}`,
    position: 'relative',
  },
  divider: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    backgroundColor: theme.palette.shades['clr-white-900'],
  },
  line: {
    width: 0,
    height: '40px',
    borderLeft: '3px solid',
    borderLeftColor: theme.palette.neutral['clr-900'],
    zIndex: 12,
    position: 'relative',
    bottom: '7px',
    left: '0.5px',
  },
  triangle: {
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: '10px solid',
    borderBottomColor: theme.palette.neutral['clr-900'],
    transform: 'rotate(180deg)',
  },
  scaleContainer: {
    width: '20px',
    height: 'fit-content',
    position: 'absolute',
    right: '-10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  '@keyframes smoothAnimation': {
    '0%': { width: '0%' },
    '100%': { width: (props) => `${props.scale}%` },
  },
  innerBar: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    animationName: '$smoothAnimation',
    animationDuration: '1s',
    animationTimingFunction: 'ease-in',
    animationFillMode: 'forwards',
    borderRadius: '4px 0px 0px 4px',
  },
  numberLabel: {
    position: 'absolute',
    top: '35px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight : 600
  },
}));

const ScoreBar = ({ scale = 5, maxScale = 5 }) => {
  const classes = useStyles({ maxScale });
  const barElementRef = React.useRef(null);

  // Create divider positions based on maxScale
  const Divider_Positions = React.useMemo(() => {
    const positions = [];
    for (let i = 0; i < maxScale; i++) {
      positions.push((i / (maxScale - 1)) * 100);
    }
    return positions;
  }, [maxScale]);

  const scaledWidth = React.useMemo(() => {
    const index = Math.floor(scale) - 1;
    const nextIndex = Math.ceil(scale) - 1;
  
    if (index === nextIndex) {
      return Divider_Positions[index];
    }
  
    const fraction = scale - Math.floor(scale);
    return (
      Divider_Positions[index] +
      (Divider_Positions[nextIndex] - Divider_Positions[index]) * fraction
    );
  }, [scale, Divider_Positions]);

  React.useEffect(() => {
    const barElement = barElementRef.current;

    if (barElement) {
      barElement.classList.remove(classes.innerBar);

      void barElement.offsetWidth;

      barElement.classList.add(classes.innerBar);
      barElement.style.width = `${scaledWidth}%`;
    }
  }, [scale]);

  return (
    <Box width="100%" my={7} display="flex" justifyContent="center">
      <Box className={classes.gradientBar}>
        {Divider_Positions.map((position, index) => (
          <Box key={index} position="relative" className={classes.divider}  style={{ left: `${position}%` }}>
            <Typography className={classes.numberLabel}>{index + 1}</Typography>
          </Box>
        ))}
        <Box ref={barElementRef}>
          <Box className={classes.scaleContainer}>
            <Box className={classes.triangle}></Box>
            <Box className={classes.line}></Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ScoreBar;